import React, { useEffect, useMemo } from "react";
import Button from "components/Buttons";
import { gql, useQuery } from "@apollo/client";
import { useCartStore } from "stores/cart/useCartStore";
import SingleSelectField from "components/ui/single-select";
import { GET_SADDLE_DETAIL_QUERY } from "apollo/queries/getSaddleDetails";
import { useBrandDisciplineStore } from "stores/brandDiscipline/useBrandDisciplineStore";
import { saddleSelectStyles, useCartActions } from "pages/SaddleDetails/utils";
import { useAuthStore } from "stores/auth/authStore";
import { Modal } from "stores/modal/useModalStore";
import Loader from "components/Spinners/Loader";
import { getBillyCookColorSelectProps } from "components/ui/BillyCookColorSelect";
import { shouldShowBillyCookColorSwatches } from "utils/billyCookColors";

const normColor = (c) => String(c ?? "").trim().toLowerCase();

export default function SeatSizeSelectInModal({ item, selectedHorse }) {
  const user = useAuthStore((s) => s.user);
  const setIsAction = useAuthStore((s) => s.setIsAction);

  const selectedVariant = useCartStore((s) => s.selectedVariant);
  const selectedSeatSize = useCartStore((s) => s.selectedSeatSize);
  const selectedColor = useCartStore((s) => s.selectedColor);
  const setSelectedVariant = useCartStore((s) => s.setSelectedVariant);
  const setSelectedSeatSize = useCartStore((s) => s.setSelectedSeatSize);
  const setSelectedColor = useCartStore((s) => s.setSelectedColor);

  const { handleAddToCart, addToCartLoading, open } = useCartActions();
  const changeActionState = useBrandDisciplineStore((a) => a.changeActionState);

  const { data, loading } = useQuery(gql(GET_SADDLE_DETAIL_QUERY), {
    variables: {
      id: item?.sk,
      horseId: selectedHorse?.value,
      brandName: item?.brandName,
    },
    skip: !item?.sk || !item?.brandName || !selectedHorse?.value,
    fetchPolicy: "cache-first",
  });

  const variants = useMemo(
    () => data?.listSaddleDetail?.filter((x) => x?.type === "VARIANT") ?? [],
    [data]
  );

  const seatSizes = useMemo(() => {
    const seen = new Set();
    const ordered = [];
    for (const row of variants) {
      const n = parseFloat(row?.size);
      if (!Number.isFinite(n) || seen.has(n)) continue;
      seen.add(n);
      ordered.push({ label: String(n), value: n });
    }
    return ordered.sort((a, b) => a.value - b.value);
  }, [variants]);

  const colors = useMemo(() => {
    const variantColors = variants.filter((row) => row?.color).map((row) => row.color);
    const productHint =
      data?.listSaddleDetail?.find((row) => row?.type === "PRODUCT")
        ?.availableColors || [];
    const seen = new Set();
    const ordered = [];
    for (const c of [...productHint, ...variantColors]) {
      const key = normColor(c);
      if (key && !seen.has(key)) {
        seen.add(key);
        ordered.push({ label: c, value: c });
      }
    }
    return ordered;
  }, [variants, data]);

  const colorsForSelectedSize = useMemo(() => {
    if (!selectedSeatSize?.value) return colors;
    const sizeNum = Number(selectedSeatSize.value);
    const available = new Set(
      variants
        .filter((row) => parseFloat(row?.size) === sizeNum && row?.color)
        .map((row) => normColor(row.color))
    );
    if (!available.size) return colors;
    return colors.filter((opt) => available.has(normColor(opt.value)));
  }, [colors, selectedSeatSize, variants]);

  const needsColor = colors.length > 0;
  const hasSeatSizes = !loading && seatSizes.length > 0;
  const canAddToCart =
    selectedVariant &&
    selectedSeatSize &&
    (!needsColor || selectedColor);

  const billyCookColorSelectProps = useMemo(
    () =>
      shouldShowBillyCookColorSwatches(item?.brandName)
        ? getBillyCookColorSelectProps("color", saddleSelectStyles)
        : {},
    [item?.brandName]
  );

  useEffect(() => {
    if (!hasSeatSizes) {
      setSelectedSeatSize(null);
      setSelectedColor(null);
      setSelectedVariant(null);
      return;
    }
    if (
      selectedSeatSize &&
      !seatSizes.find((o) => o.value === selectedSeatSize?.value)
    ) {
      setSelectedSeatSize(null);
      setSelectedColor(null);
      setSelectedVariant(null);
    }
  }, [
    hasSeatSizes,
    seatSizes,
    selectedSeatSize,
    setSelectedSeatSize,
    setSelectedColor,
    setSelectedVariant,
  ]);

  // Pre-fill from the listing card the user clicked.
  useEffect(() => {
    if (!hasSeatSizes || !item) return;

    if (!selectedSeatSize && item?.size != null) {
      const n = parseFloat(item.size);
      const match = seatSizes.find((o) => o.value === n);
      if (match) setSelectedSeatSize(match);
    }

    if (!selectedColor && item?.color) {
      const match = colors.find((o) => normColor(o.value) === normColor(item.color));
      if (match) setSelectedColor(match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSeatSizes, item, seatSizes, colors]);

  useEffect(() => {
    if (!selectedSeatSize?.value) {
      setSelectedVariant(null);
      return;
    }
    if (needsColor && !selectedColor?.value) {
      setSelectedVariant(null);
      return;
    }

    const sizeNum = Number(selectedSeatSize.value);
    const match = variants.find(
      (row) =>
        parseFloat(row?.size) === sizeNum &&
        (!needsColor || normColor(row?.color) === normColor(selectedColor?.value))
    );
    setSelectedVariant(match ?? null);
  }, [
    selectedSeatSize,
    selectedColor,
    variants,
    needsColor,
    setSelectedVariant,
  ]);

  useEffect(() => {
    if (
      selectedSeatSize &&
      colorsForSelectedSize.length === 1 &&
      !selectedColor
    ) {
      setSelectedColor(colorsForSelectedSize[0]);
    }
  }, [selectedSeatSize, colorsForSelectedSize, selectedColor, setSelectedColor]);

  useEffect(() => {
    if (
      !selectedColor?.value ||
      !colorsForSelectedSize.length ||
      colorsForSelectedSize.some(
        (opt) => normColor(opt.value) === normColor(selectedColor.value)
      )
    ) {
      return;
    }
    setSelectedColor(null);
    setSelectedVariant(null);
  }, [colorsForSelectedSize, selectedColor, setSelectedColor, setSelectedVariant]);

  const handleAddToCartInModal = () => {
    const variant = selectedVariant;
    if (!variant?.sk) return;

    if (user) {
      handleAddToCart(
        variant,
        selectedHorse,
        changeActionState,
        open,
        variant?.score,
        true
      );
    } else {
      setIsAction("buy");
      open(Modal.Login);
    }
  };

  return (
    <div className="flex flex-col gap-y-4 items-center">
      {loading && <Loader />}

      {hasSeatSizes && (
        <SingleSelectField
          name="size"
          label="Seat Size"
          placeholder="Select Size"
          height="40px"
          options={seatSizes}
          className="!h-auto relative w-full lg:!w-[200px]"
          value={selectedSeatSize}
          stylesOverride={saddleSelectStyles}
          onChange={(value) => {
            setSelectedSeatSize(value);
            setSelectedColor(null);
            setSelectedVariant(null);
          }}
        />
      )}

      {needsColor && hasSeatSizes && (
        <SingleSelectField
          name="color"
          label="Color"
          placeholder="Select Color"
          height="40px"
          options={colorsForSelectedSize}
          className="!h-auto relative w-full lg:!w-[200px]"
          value={selectedColor}
          stylesOverride={
            billyCookColorSelectProps.stylesOverride ?? saddleSelectStyles
          }
          disabled={!selectedSeatSize}
          onChange={(value) => {
            setSelectedColor(value);
          }}
          components={billyCookColorSelectProps.components}
        />
      )}

      {canAddToCart && (
        <Button
          primary
          whiteOutline
          className="addToCart_btn"
          onClick={handleAddToCartInModal}
          loading={addToCartLoading === selectedVariant?.sk}
          disabled={!user || !!addToCartLoading}
        >
          Add to Cart
        </Button>
      )}
    </div>
  );
}
